// Sign-in for the desktop app through this site (RFC 8252 loopback with PKCE).
//
// The app opens /desktop?port=…&state=…&challenge=…. The request is kept in this
// tab's session storage, so it survives the trip through sign-up, sign-in or
// completing the details. Once the account is complete, /desktop asks the API
// for a one-time code and sends the browser to the app's loopback address.

export interface DesktopRequest {
  port: number;
  state: string;
  challenge: string;
  /** The tab was already signed in when the app asked, so the customer confirms the account first. */
  confirm: boolean;
  createdAt: number;
}

const KEY = "frameflow.desktop-request";
/** This tab's session storage; tests pass their own. */
type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;
/** The app waits ten minutes for the browser before it gives up. */
const MAX_AGE_MS = 10 * 60 * 1000;

const STATE = /^[A-Za-z0-9_-]{16,64}$/;
const CHALLENGE = /^[A-Za-z0-9_-]{43}$/;

/** Reads the app's request from a /desktop query string, or null if it is malformed. */
export function parseDesktopRequest(search: string, now = Date.now()): Omit<DesktopRequest, "confirm"> | null {
  const params = new URLSearchParams(search);
  const port = Number(params.get("port"));
  const state = params.get("state") ?? "";
  const challenge = params.get("challenge") ?? "";
  if (!Number.isInteger(port) || port < 1024 || port > 65535 || !STATE.test(state) || !CHALLENGE.test(challenge)) {
    return null;
  }
  return { port, state, challenge, createdAt: now };
}

export function rememberDesktopRequest(request: DesktopRequest, store: Store = sessionStorage): void {
  store.setItem(KEY, JSON.stringify(request));
}

/** The app's request waiting in this tab, if it is still fresh. */
export function pendingDesktopRequest(now = Date.now(), store: Store = sessionStorage): DesktopRequest | null {
  try {
    const stored = JSON.parse(store.getItem(KEY) ?? "null") as DesktopRequest | null;
    if (stored && now - stored.createdAt < MAX_AGE_MS && Number.isInteger(stored.port) && CHALLENGE.test(stored.challenge)) {
      return stored;
    }
  } catch {
    // Unreadable storage counts as no request.
  }
  return null;
}

export function forgetDesktopRequest(store: Store = sessionStorage): void {
  store.removeItem(KEY);
}

/** Where the browser goes to hand the code to the app. Only the loopback address, never another host. */
export function loopbackUrl(request: Pick<DesktopRequest, "port" | "state">, code: string): string {
  const url = new URL(`http://127.0.0.1:${request.port}/callback`);
  url.searchParams.set("code", code);
  url.searchParams.set("state", request.state);
  return url.toString();
}
